import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@core-ui/Components/ui/badge';
import { Button } from '@core-ui/Components/ui/button';
import { cn } from '@core-ui-lib/utils';

import { Loader2, Download, CirclePlus } from 'lucide-react';

import ContactListUploadPanel from '../inline-create/ContactListUploadPanel';

import { SearchInput } from '@ui/shared/searchInput';
import { usePaginationData } from '@hooks/usePaginationData';
import { PaginationControls } from '@ui/shared/paginationControls';
import useContactsApi from '@features/contacts/hooks/useContactsApi';
import { useSessionStore } from '@features/auth';

import type { ContactListDto } from '@features/wizard';
import useWizardStore from '../../store/useWizardStore';
import ContactsPreviewModal from '../ContactsPreviewModal';
import { useOutreachViewModel } from '@features/contacts/hooks/useOutreachViewModel.ts';
import { logInfo } from '@lib/logger/logInfo.ts';
import { logSuccess } from '@lib/logger/logSuccess.tsx';
import { logError } from '@lib/logger/logError.ts';
import { type ContactsTab } from '@features/contacts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core-ui/Components/ui/tabs.tsx';
import { useFeatureFlagsStore } from '@store/featureFlags';
import SalesforceLists from '@features/wizard/components/lists/salesforceLists.tsx';
import SessionsTabPanel from '@features/wizard/components/sessions/SessionsTabPanel';
import { useHubspotViewModel } from '@features/contacts/hooks/useHubspotViewModel.ts';
import { Integrations, IntegrationsContactTypes } from '@features/Settings/enums/IntegrationsEnums.ts';
import { useSalesforceViewModel } from '@features/contacts/hooks/useSalesforceViewModel.ts';
import type { OutreachAccount } from '@types';
import { IntegrationsListsFallback } from '@features/contacts/components/IntegrationsListsFallback.tsx';
import { ContactListItemSkeleton } from '@features/contacts/components/ContactListSidebar.tsx';
import {
  defineIntegrationIsConfigured,
  getIntegrationName,
  selectIcon,
  toastNoAccountWarning
} from '@utils/intregrationsUIHelpers.ts';

export default function SelectContactsStep (): React.JSX.Element {
  const { t } = useTranslation();
  const featureFlags = useFeatureFlagsStore((s) => s.flags);
  const { currentUser } = useSessionStore();
  const organizationId = String(currentUser?.organizationId ?? '').trim();

  const { getContactsByContactListIdAsync, exportContactsOfListToCsv, getCsvListsByOrgIdAsync } = useContactsApi();
  const { retrieveOutreachLists, retrieveOutreachAccounts } = useOutreachViewModel();
  const { retrieveHubspotContactsLists, retrieveHubspotAccounts } = useHubspotViewModel();
  const { retrieveSalesforceLists, retrieveSalesforceAccounts } = useSalesforceViewModel();

  const outreachAccountsCount = useRef(0);
  const hubspotAccountsCount = useRef(0);
  const salesforceAccountsCount = useRef(0);
  const { selectedContactList, setContactList, currentContactsTab, setContactsTab, setOutreachData, setSelectedFolder, setContactsCount } = useWizardStore();
  const [contactLists, setContactLists] = useState<ContactListDto[]>([]);
  const [filteredLists, setFilteredLists] = useState<ContactListDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [previewListId, setPreviewListId] = useState<string | null>(null);
  const [downloadingListId, setDownloadingListId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ContactsTab>(currentContactsTab ?? 'csv');
  const [outreachContactsParams, setOutreachContactsParams] = useState({
    nonce: '',
    stepId: 0,
    sequenceId: 0,
    accountsCount: 0
  });

  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const [totalCsvContactLists, setTotalCsvContactLists] = useState(0);

  const [outreachConfigured, setOutreachConfigured] = useState( featureFlags.outreach && outreachAccountsCount.current !== 0);
  const [hubspotConfigured, setHubspotConfigured] = useState( featureFlags.hubspot && hubspotAccountsCount.current !== 0);
  const [salesforceConfigured, setSalesforceConfigured] = useState(featureFlags.salesforce && salesforceAccountsCount.current !== 0);

  const isCsvTab = activeTab === 'csv';
  
  const retrieveCsvLists = useCallback(async (
    page = '1',
    pageLimit = '100',
    searchQueryParam = '',
  ): Promise<ContactListDto[] | undefined> => {
    const response = await getCsvListsByOrgIdAsync(organizationId, page, pageLimit, searchQueryParam);
    if (!response.success) {
      return undefined;
    }
    setTotalCsvContactLists(response.totalCount ?? response.data?.length ?? 0);
    return response.data;
  }, [getCsvListsByOrgIdAsync, organizationId]);

  const loadCsvContactsPage = useCallback(async (page: string, pageLimit: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await retrieveCsvLists(page, pageLimit, searchQuery);
      const fetchedLists = response ?? [];
      setContactLists(fetchedLists);
      setFilteredLists(fetchedLists);
      setContactsCount(fetchedLists.length);
    } finally {
      setIsLoading(false);
    }
  }, [retrieveCsvLists, searchQuery]);

  const {
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    currentData: paginatedLists,
    totalItems,
  } = usePaginationData<ContactListDto>({
    mode: isCsvTab ? 'server' : 'client',
    data: filteredLists,
    totalItems: totalCsvContactLists,
    onServerRequest: useCallback((page: number, limit: number) => {
      void loadCsvContactsPage(String(page), String(limit));
    }, [searchQuery]),
    initialRowsPerPage: 5,
  });

  const listsToRender = isCsvTab ? filteredLists : paginatedLists;

  const handleSelectList = (list: ContactListDto): void => {
    switch (activeTab) {
    case 'outreach' :
      setOutreachContactsParams({
        ...outreachContactsParams,
        stepId: Number(list.sequenceId),
        sequenceId: Number(list.id)
      });

      if (list.sequenceId !== undefined) {
        setOutreachData(
          {
            outreachSequenceId: list.sequenceId?.toString(),
            outreachSequenceStep: list.id,
            nonce: outreachContactsParams.nonce,
          }
        );
      }
      break;
    }
    

    if (selectedContactList?.id === list.id) {
      setContactList(null);
    } else {
      setContactList(list);
    }
  };

  const handlePreviewContacts = (list: ContactListDto): void => {
    if (activeTab === 'outreach') {

      setOutreachData({
        outreachSequenceId: list.sequenceId?.toString() ?? '',
        outreachSequenceStep: list.id,
        nonce: outreachContactsParams.nonce ?? '',
      });
    }

    setPreviewListId(list.id);
  };

  const handleDownloadCsv = async (list: ContactListDto): Promise<void> => {
    setDownloadingListId(list.id);
    const result = await getContactsByContactListIdAsync(list.id);
    if (result.success && result.data !== undefined) {
      exportContactsOfListToCsv({
        contacts: result.data,
        filename: list.title,
      });
    }
    setDownloadingListId(null);
  };

  /**
   * Strategy Callback to select the appropriate fetch function based on the active tab,
   * they must return ContactListDto[] | undefined to avoid further data adaptations.
   * Add cases for other integrations when needed.
   **/
  const selectContactsListsFetchCallback = useCallback(async ()=> {
    switch (activeTab) {
    case 'outreach': {
      const accounts = await retrieveOutreachAccounts();
      const hasAccounts = Array.isArray(accounts) && accounts.length > 0;
      setOutreachConfigured(hasAccounts);

      if (!hasAccounts) {
        await toastNoAccountWarning(t('contacts.toast.noIntegration'));
        throw new Error('No Outreach accounts linked');
      }

      return await retrieveOutreachLists();
    }
    case 'hubspot': {
      const accounts = await retrieveHubspotAccounts();
      const hasAccounts = Array.isArray(accounts) && accounts.length > 0;
      setHubspotConfigured(hasAccounts);

      if (!hasAccounts) {
        await toastNoAccountWarning(t('contacts.toast.noIntegration'));
        throw new Error('No Hubspot accounts linked');
      }

      return await retrieveHubspotContactsLists();
    }
    case 'salesforce': {
      const accounts = await retrieveSalesforceAccounts();
      const hasAccounts = Array.isArray(accounts) && accounts.length > 0;
      setSalesforceConfigured(hasAccounts);

      if (!hasAccounts) {
        await toastNoAccountWarning(t('contacts.toast.noIntegration'));
        throw new Error('No Salesforce accounts linked');
      }

      return await retrieveSalesforceLists();
    }
    default: return await retrieveCsvLists('1', String(rowsPerPage), searchQuery);
    }
  }, [setActiveTab, retrieveOutreachAccounts, retrieveOutreachLists, retrieveHubspotAccounts, retrieveHubspotContactsLists, retrieveSalesforceAccounts, retrieveSalesforceLists, retrieveCsvLists, t]);

  const getContactLists = useCallback(async (): Promise<void> => {
    await logInfo(
      'Loading contact lists...',
      'ContactsVM.refreshContactLists',
      { organizationId, activeTab },
      false);

    setContactLists([]);
    setContactList(null);
    setCurrentPage(1);

    if (activeTab === 'csv') {
      // CSV lists are fetched by the server-mode pagination effect (usePaginationData ->
      // loadCsvContactsPage), which is the single source of truth for this tab and owns
      // isLoading for it. Fetching here too would double-call the v2 lists endpoint.
      return;
    }

    try {
      const response = await selectContactsListsFetchCallback();
      if (response === undefined) {
        await logError('Error loading contact lists', getContactLists.name, { error: response }, false);
      }

      const fetchedContactLists = response ?? [];
      setContactLists(fetchedContactLists);
      setFilteredLists(fetchedContactLists);
      setContactsCount(fetchedContactLists.length);
      setIsLoading(false);

      await logSuccess('Contact lists loaded', 'ContactsVM.refreshContactLists', { count: fetchedContactLists.length }, false);
    } catch (error) {
      setContactLists([]);
      setIsLoading(false);
      await logError(
        'Error loading contact lists',
        'ContactsVM.refreshContactLists',
        { organizationId, activeTab, error: String((error as Error)?.message ?? error) },
        false,
      );
    }
  }, [organizationId, activeTab, selectContactsListsFetchCallback, setContactList, setContactsCount]);

  const evaluateIfLinkedAccounts = (): boolean => {
    switch (activeTab.toUpperCase() as IntegrationsContactTypes) {
    case IntegrationsContactTypes.Outreach: return outreachConfigured;
    case IntegrationsContactTypes.Hubspot: return hubspotConfigured;
    case IntegrationsContactTypes.Salesforce: return salesforceConfigured;
    default: return false;
    }
  };

  useEffect(() => {
    void defineIntegrationIsConfigured(Integrations.Outreach.toLowerCase(), featureFlags, retrieveOutreachAccounts, setOutreachConfigured);
    void defineIntegrationIsConfigured(Integrations.Hubspot.toLowerCase(), featureFlags, retrieveHubspotAccounts, setHubspotConfigured);
    void defineIntegrationIsConfigured(Integrations.Salesforce.toLowerCase(), featureFlags, retrieveSalesforceAccounts, setSalesforceConfigured);
  }, []);

  useEffect(() => {
    const fetchLists = async (): Promise<void> => {
      if (currentUser?.organizationId === undefined) {
        return;
      }
      if (activeTab === 'sessions') {
        // The Sessions tab manages its own data flow via useSessionsViewModel.
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      await getContactLists();
      if (activeTab !== 'csv') {
        setIsLoading(false);
      }
    };

    void fetchLists();
  }, [currentUser?.organizationId, activeTab]);

  useEffect(() => {
    if (isCsvTab) {
      return;
    }
    if (searchQuery.trim() === '') {
      setFilteredLists(contactLists);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredLists(
        contactLists.filter((list) => list.title.toLowerCase().includes(query)),
      );
    }
  }, [searchQuery, contactLists, isCsvTab]);

  useEffect(() => {
    const setOutreachAccountParams: () => Promise<void> = async ()=>{
      const response = await retrieveOutreachAccounts();
      const nonce = (response as OutreachAccount[])[0].nonce;
      const accountsCount = (Array.isArray(response)) ? (response ).length : 0;
      setOutreachContactsParams({ ...outreachContactsParams, nonce, accountsCount });
      setOutreachConfigured(featureFlags.outreach && accountsCount !== 0);
    };

    void setOutreachAccountParams();
  }, []);


  useEffect(() => {
    const nextTab = currentContactsTab ?? 'csv';
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
    if (nextTab !== currentContactsTab) {
      setContactsTab(nextTab);
    }
    if (nextTab !== 'salesforce') {
      setSelectedFolder('');
    }
  }, [activeTab, currentContactsTab, setContactsTab, setSelectedFolder]);

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold">{t('wizard.selectContacts.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('wizard.selectContacts.description')}</p>
      </div>

      <Tabs
        className="w-full"
        value={activeTab}
        onValueChange={(tabValue) => {
          setActiveTab(tabValue as ContactsTab);
          setContactsTab(tabValue as ContactsTab);
        }}>
        <TabsList data-qa-id="contacts-tab-list"  className="bg-transparent flex w-full md:w-fit p-1 rounded-xl gap-2">
          <TabsTrigger value="csv" className="tab-trigger px-4 py-1.5 rounded-full">CSV</TabsTrigger>
          {featureFlags.contactsFilter && (
            <TabsTrigger value="sessions" data-qa-id="sessions-tab-trigger" className="tab-trigger px-4 py-1.5 rounded-full">{t('wizard.selectContacts.sessions.tabLabel')}</TabsTrigger>
          )}
          {featureFlags.outreach && (
            <TabsTrigger value="outreach" className="tab-trigger px-4 py-1.5 rounded-full">Outreach</TabsTrigger>
          )}
          {featureFlags.hubspot && (
            <TabsTrigger  value="hubspot" className="tab-trigger px-4 py-1.5 rounded-full">Hubspot</TabsTrigger>
          )}
          {featureFlags.salesforce && (
            <TabsTrigger value="salesforce" className="tab-trigger px-4 py-1.5 rounded-full">Salesforce</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {activeTab === 'sessions' ? (
            <SessionsTabPanel />
          ) : (
            <div className="space-y-4">
              {activeTab !== 'salesforce' ? (
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <SearchInput
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                      onClear={() => {
                        setSearchQuery('');
                      }}
                      placeholder={t('wizard.selectContacts.searchPh')}
                    />
                  </div>
                  {activeTab === 'csv' && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setIsUploadPanelOpen(true); 
                      }}
                      className="gap-1 bg-primary hover:bg-primary/90 text-primary-foreground whitespace-nowrap"
                    >
                      <CirclePlus className="h-4 w-4" />
                      {t('wizard.inlineCreate.contacts.uploadNewList', 'Upload New List')}
                    </Button>
                  )}
                </div>
              ) : null
              }

              {activeTab === 'csv' && (
                <ContactListUploadPanel
                  isOpen={isUploadPanelOpen}
                  onClose={() => {
                    setIsUploadPanelOpen(false); 
                  }}
                  onCreated={(newList) => {
                    setIsUploadPanelOpen(false);
                    // This panel only renders on the csv tab, so refresh via the same
                    // pagination-aware fetcher used elsewhere for csv, rather than
                    // getContactLists (which now intentionally skips csv fetching).
                    void loadCsvContactsPage(String(currentPage), String(rowsPerPage));
                    setContactList(newList);
                  }}
                />
              )}

              {isLoading ? (
                <ContactListItemSkeleton rows={5} />

              ) : filteredLists.length === 0 ? (
                activeTab !== 'csv' && !evaluateIfLinkedAccounts() ? (
                  <IntegrationsListsFallback
                    icon={selectIcon(activeTab)}
                    message={t('contacts.table.noAccounts')}
                    buttonText={t('contacts.table.gotoIntegration', { integration: getIntegrationName(activeTab) })}
                    redirectionUrl={'../settings/app'}
                  />
                ) : (
                  <div className="px-4 py-10 text-center text-sm text-muted-foreground"></div>
                )

              ) : activeTab !== 'salesforce' ? (
                <>
                  <div className="rounded-lg border border-border">
                    {listsToRender.map((list: ContactListDto) => (
                      <button
                        key={list.id}
                        type="button"
                        onClick={() => {
                          handleSelectList(list);
                        }}
                        className={cn(
                          'w-full flex items-start gap-4 p-4 text-left transition-colors hover:bg-muted/40 border-b border-border/60 last:border-b-0',
                          selectedContactList?.id === list.id && 'bg-muted/60',
                        )}
                      >
                        <div
                          className={cn(
                            'mt-1 h-5 w-5 rounded-full border-2 flex-shrink-0 transition-colors',
                            selectedContactList?.id === list.id
                              ? 'border-primary bg-primary'
                              : 'border-muted-foreground',
                          )}
                        >
                          {selectedContactList?.id === list.id && (
                            <div className="h-full w-full flex items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{list.title}</span>
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-sm">
                            <button
                              type="button"
                              disabled={(selectedContactList?.id !== list.id || selectedContactList === null) && activeTab === 'outreach'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewContacts(list);
                              }}
                              className="text-primary hover:underline disabled:opacity-50 disabled:text-muted disabled:hover:no-underline transition-colors"
                            >
                              {t('wizard.selectContacts.viewContacts')}
                            </button>
                            {activeTab === 'csv' &&
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDownloadCsv(list);
                              }}
                              disabled={downloadingListId === list.id}
                              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            >
                              {downloadingListId === list.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3" />
                              )}
                              {t('wizard.selectContacts.downloadCsv')}
                            </button>
                            }
                          </div>
                        </div>

                        {selectedContactList?.id === list.id && (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            {t('wizard.selected')}
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>

                  {totalItems > 0 && (
                    <PaginationControls
                      totalItems={totalItems}
                      currentPage={currentPage}
                      onPageChange={(p) => {
                        setCurrentPage(Math.max(1, Math.min(p, Math.ceil(totalItems / rowsPerPage))));
                      }}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={(n) => {
                        setRowsPerPage(n);
                        setCurrentPage(1);
                      }}
                      rowsPerPageOptions={[5, 10, 15]}
                    />
                  )}
                </>

              ) : (
                !evaluateIfLinkedAccounts() ? (
                  <IntegrationsListsFallback
                    icon={selectIcon(activeTab)}
                    message={t('contacts.table.noAccounts')}
                    buttonText={t('contacts.table.gotoIntegration', { integration: getIntegrationName(activeTab) })}
                    redirectionUrl={'../settings/app'}
                  />
                ) : (
                  <SalesforceLists />
                )
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ContactsPreviewModal
        isOpen={previewListId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewListId(null);
          }
        }}
        contactListId={previewListId}
        contactListTitle={contactLists.find((l) => l.id === previewListId)?.title ?? ''}
        activeTab={activeTab}
        outreachContacts={outreachContactsParams}
      />
    </div>
  );
}

